import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { CompanyIcon } from 'src/assets/icons/companyIcon';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { PATH_HOME } from 'src/constants/spa-routes';
import { CompanySelectModal } from 'src/components/CompanySelectComponent/CompanySelectModal';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { persistor, useAppSelector, useAppDispatch } from 'src/store';
import { selectManagedAccountId, setIsCompanyChanged } from 'src/store/slices/companySlice';
import { Drawer } from 'vaul';

// In the mobile drawer, picking an account also closes the drawer.
const AccordionContentItem = ({
  children,
  className = '',
  isLgDown,
}: {
  children: React.ReactElement;
  className?: string;
  isLgDown: boolean;
}) => {
  return isLgDown ? (
    <Drawer.Close asChild className={className}>
      {children}
    </Drawer.Close>
  ) : (
    children
  );
};

export const CompanySelectComponent = ({
  disabled = false,
  isAccordion = false,
  isLgDown = false,
}) => {
  const { managedAccountId } = useAppSelector((s) => s.companySlices);

  const { data: userData } = useGetUsersMeQuery();

  const dispatch = useAppDispatch();
  const storageSelectedAccount = userData?.managed_accounts?.find(
    (el) => el?.account?.id === Number(managedAccountId),
  )?.account;

  useEffect(() => {
    if (userData?.managed_accounts) {
      if (!storageSelectedAccount) {
        dispatch(selectManagedAccountId(''));
        dispatch(setIsCompanyChanged(false));
        localStorage.removeItem('COMPANY_ID');
      }
    }
  }, [userData?.managed_accounts]);

  // The menu passes numeric account ids; the modal passes its radio value.
  const changeAccountHandler = async (accountId: string | number) => {
    localStorage.setItem('COMPANY_ID', String(accountId));
    localStorage.removeItem('API_VERSION');
    dispatch(selectManagedAccountId(accountId));
    if (!accountId) {
      persistor.flush().then(() => {
        return persistor.purge();
      });
    }
    globalThis.location.assign(PATH_HOME.ROOT);
  };

  // todo: use this function when migration to v2 will be done completely, together with the
  // values it uses:
  // const storageCompanyKey = window.localStorage.getItem('persist:company');
  // const navigate = useNavigate();
  // const location = useLocation();
  // const isAccountLocation = location.pathname.includes(PATH_DASHBOARD.ACCOUNT);
  // const isSubscriptionLocation = location.pathname.includes(PATH_DASHBOARD.SUBSCRIPTION);
  // const handleRefetchAllQuery = () => {
  //   dispatch(baseApi.util.invalidateTags(refetchTagsWithAccountParam));
  //   if (isSubscriptionLocation) {
  //     dispatch(baseApi.util.invalidateTags(refetchTagsWithAccountParamForSubscription));
  //   }
  // };
  // const changeAccountHandler = async (accountId) => {
  //   if (!isAccountLocation && !isSubscriptionLocation) {
  //     await new Promise((resolve) => {
  //       localStorage.setItem('COMPANY_ID', accountId);
  //       navigate(PATH_HOME.ROOT);
  //       setTimeout(resolve, 0);
  //     });
  //   }
  //   dispatch(selectManagedAccountId(accountId));
  //   if (!isSubscriptionLocation) {
  //     if (String(accountId) === JSON.parse(storageCompanyKey)?.managedAccountId) {
  //       dispatch(setIsCompanyChanged(false));
  //     } else {
  //       dispatch(setIsCompanyChanged(true));
  //     }
  //   }
  //   handleRefetchAllQuery();
  //   if (!accountId) {
  //     persistor.flush().then(() => {
  //       return persistor.purge();
  //     });
  //   }
  // };

  return isAccordion ? (
    <Accordion disabled={disabled} type="single" collapsible className="w-full">
      <AccordionItem value="companySelect" className="border-b-0">
        <AccordionTrigger
          className={clsx(
            !disabled
              ? '[&_p]:hover:opacity-100 [&_svg:last-child]:hover:opacity-100'
              : 'opacity-40',
            'rounded-sm-5 w-full [&[data-state=open]]:bg-woodsmoke [&_svg:first-child]:hover:opacity-100 [&_svg:first-child]:opacity-50 [&_p]:transition-all [&_svg]:transition-all [&[data-state=open]_p]:opacity-100 [&[data-state=open]_svg]:opacity-100 [&_svg:last-child]:opacity-50 [&_p]:opacity-60',
          )}
        >
          <div className="flex items-center gap-2.5">
            <CompanyIcon fill="#FFFFFF99" fillInner={'#05050D'} />
            <p className="text-white text-lg font-medium">
              {storageSelectedAccount ? storageSelectedAccount.company_name : 'Company'}
            </p>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="ml-11 mt-2 flex flex-col gap-1.5 relative">
            {userData?.managed_accounts?.map((el) => {
              const activeAccount = Number(managedAccountId) === Number(el?.account.id);
              return (
                <AccordionContentItem key={el?.id} isLgDown={isLgDown}>
                  <div
                    className={clsx(
                      activeAccount ? '[&_span]:opacity-100' : '[&_span]:opacity-60',
                      '[&_span]:hover:opacity-100 [&_span]:transition-all relative text-left cursor-pointer',
                    )}
                  >
                    <button
                      type="button"
                      className="py-2 text-left w-full"
                      onClick={() => changeAccountHandler(el?.account?.id)}
                    >
                      <span
                        className={clsx(
                          activeAccount && 'font-bold',
                          'text-white font-medium text-base',
                        )}
                      >
                        {el?.account?.company_name}
                      </span>
                    </button>
                  </div>
                </AccordionContentItem>
              );
            })}
            <AccordionContentItem
              isLgDown={isLgDown}
              className={clsx(Boolean(!managedAccountId) && 'pointer-events-none opacity-40')}
            >
              <div
                className={clsx(
                  '[&_span]:opacity-60',
                  '[&_span]:hover:opacity-100 [&_span]:transition-all relative text-left cursor-pointer',
                )}
              >
                <button
                  type="button"
                  className="py-2 text-left w-full"
                  onClick={() => changeAccountHandler('')}
                >
                  <span className={clsx('text-white font-medium text-base')}>Reset</span>
                </button>
              </div>
            </AccordionContentItem>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  ) : (
    <CompanySelectModal
      disabled={disabled}
      accounts={userData?.managed_accounts}
      changeAccountHandler={changeAccountHandler}
      storageSelectedAccount={storageSelectedAccount}
    />
  );
};
